// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import TextField from "@ces/MobiusOverrides/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@ces/MobiusOverrides/Tooltip/Tooltip";
import IconStepperAdd from "@ces/XDL/Icons/Add";
import IconStepperSubtract from "@ces/XDL/Icons/Minus";
import TooltipIcon from "@ces/XDL/Icons/TooltipIcon";
import { mergeWithCss, xDefaultRules, xMask } from "@ces/XDL/Utilities";
import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useMemo, useState } from "react";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    showMultipleSaleQtyTooltip?: boolean;
    min?: number | string;
    validationRules?: string;
    minimumOrderQty?: number;
    multipleSaleQty?: number;
    qtyPerBaseUnitOfMeasure?: number;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    container?: InjectableCss;
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
    actionButton?: ClickablePresentationProps;
    questionIconStyles?: IconProps;
    multipleSaleQtyWrapper?: InjectableCss;
    multipleSaleQtyToolTip?: TooltipPresentationProps;
    print?: InjectableCss;
    qtyLabelNotEditable?: TypographyProps;
    tooltipBody?: InjectableCss;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    qtyLabelContainer?: InjectableCss;
    tooltipText?: TypographyPresentationProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    allowZero = false,
    editable = true,
    showLabel = true,
    showMultipleSaleQtyTooltip = false,
    min = 0,
    minimumOrderQty,
    multipleSaleQty,
    qtyPerBaseUnitOfMeasure,
    ...qtyFieldsProps
}: Props) => {
    const styles = useMemo(() => mergeWithCss(qtyFieldStyles, extendedStyles), []);
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);
    const increment = Math.max(
        Number(minimumOrderQty ?? 1),
        Number(qtyPerBaseUnitOfMeasure ?? 1),
        Number(multipleSaleQty ?? 1),
        1,
    );

    let minQty = 0;
    let maxQty = 999999;
    xDefaultRules["qty"].split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = +val;
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        // Xng-Changes: CES-889 - show ellipsis on 7 digits
        event.currentTarget.value = event.currentTarget.value.trim();
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    // Xng-Changes: CES-889 - show ellipsis on 7 digits
    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, blur?: boolean) => {
        event.target.value = xMask.qty(event.target.value);
        if (blur === true && event.currentTarget.value.length === 7) {
            event.currentTarget.value += "      ";
        }
        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.currentTarget) {
            let quantityValue = Number(event.currentTarget.value);
            event.currentTarget.value = xMask.qty(quantityValue.toString());
            const input = event.currentTarget;
            setTimeout(() => {
                if (input) {
                    input.setSelectionRange(0, 0);
                }
            }, 0);
            // Xng-Changes: CES-889 - show ellipsis on 7 digits
            onChangeHandler(event, true);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - increment >= 0
                ? Number(currentValue) - increment
                : undefined;

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + increment <= maxQty
                ? Number(currentValue) + increment
                : undefined;

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const multipleSaleTooltip = showMultipleSaleQtyTooltip && (
        <StyledWrapper {...styles.multipleSaleQtyWrapper}>
            <Tooltip
                appendTo={document.body}
                text={
                    <StyledWrapper>
                        <Typography as="p" {...styles.tooltipText}>
                            {siteMessage("Custom_MultipleSaleQtyTitleMsg", "Multiple Sale Quantity")}
                        </Typography>
                        <StyledWrapper {...styles.tooltipBody}>
                            {!!minimumOrderQty && !multipleSaleQty && (
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Min. Sell Pack:")}
                                    text={
                                        siteMessage(
                                            "Custom_MultipleSaleQtyTooltip_MinSellPackValue",
                                            minimumOrderQty.toString(),
                                        ) as string
                                    }
                                />
                            )}
                            {!!multipleSaleQty && (
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Multiples of:")}
                                    text={
                                        siteMessage(
                                            "Custom_MultipleSaleQtyTooltip_MultiplesOfValue",
                                            multipleSaleQty.toString(),
                                        ) as string
                                    }
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                }
                triggerComponent={<Icon {...styles.questionIconStyles} src={TooltipIcon} />}
                {...styles.multipleSaleQtyToolTip}
            />
        </StyledWrapper>
    );

    if (showActionButton && editable) {
        return (
            <StyledWrapper {...styles.container} data-test-selector="qty-field">
                {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                <StyledWrapper {...styles.qtyLabelContainer}>
                    <VisuallyHidden as="label">{translate("QTY")}</VisuallyHidden>
                    <StyledWrapper {...styles.wrapper}>
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) <= Math.max(minQty, increment)}
                            onClick={onDecreaseClickHandler}
                            data-test-selector="product_qtyOrdered_decrease"
                            aria-label="decrease quantity"
                        >
                            <Icon src={IconStepperSubtract} size={8} />
                        </Clickable>
                        <TextField
                            type="qty"
                            disabled={disabled}
                            value={currentValue}
                            onChange={onChangeHandler}
                            onBlur={onBlurHandler}
                            onClick={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            {...styles.editableQuantityTextField}
                            showError={false}
                            {...qtyFieldsProps}
                            aria-label="qty"
                        />
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) >= maxQty}
                            onClick={onIncreaseClickHandler}
                            data-test-selector="product_qtyOrdered_increase"
                            aria-label="increase quantity"
                        >
                            <Icon src={IconStepperAdd} size={8} />
                        </Clickable>
                    </StyledWrapper>

                    {/* Add multiple qty tooltip */}
                    {multipleSaleTooltip}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    if (editable) {
        return (
            <>
                <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
                {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                <TextField
                    type="qty"
                    disabled={disabled}
                    value={currentValue}
                    onChange={onChangeHandler}
                    onBlur={onBlurHandler}
                    onClick={onClickHandler}
                    onKeyDown={onKeyDownHandler}
                    {...styles.editableQuantityTextField}
                    {...qtyFieldsProps}
                />
            </>
        );
    }

    return (
        <>
            <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
            {showLabel && <Typography {...styles.qtyLabelNotEditable}>{labelOverride || translate("QTY")}</Typography>}
            <Typography {...styles.valueText}>
                {currentValue} {multipleSaleTooltip}
            </Typography>
        </>
    );
};

export default QtyField;
